import React, { useState } from "react";

import {
  Toolbar,
  Grid,
  Avatar,
  Typography,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  IconButton,
  Badge,
  ListItemText,
} from "@mui/material";
import { AccountCircle, ExitToApp } from "@mui/icons-material";
import NavigationBarStyles from "./NavigationBar.Styles";
import { useNavigate } from "react-router-dom";
import useLogout from "@Hooks/useLogout";
import { styled } from "@mui/system";
import LogoTitle from "@Components/LogoTitle/LogoTitle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useEffect } from "react";
import useGetAll from "@Hooks/useGetAll";
import { NOTIFICATIONS_URL, GET_FIRST_TEN_NOTIFICATIONS } from "@API/APIRoutes";
import usePostItem from "@Hooks/usePostItem";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import { formatNotifications } from "@Utils/Validations/HelperFunctions/FormaterFunctions";
import { useSelector } from "react-redux";
import { encryptId } from "../../Utils/SecurityFunctions";

const UserMenu = styled(Menu)(({ theme }) => ({
  "& .menuPaper": {
    width: theme.spacing(18),
    boxShadow: theme.shadows.main,
  },
  "& .menuList": {
    padding: 0,
  },
}));

const NavigationBar = ({ text, user, isDisabled }) => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const { loading, getAll } = useGetAll();
  const { loading: postLoading, postItem } = usePostItem();
  const [totalUnreadNotifications, setTotalUnreadNotifications] = useState(0);

  useEffect(() => {
    if (user.roles[0] !== "ADMIN") {
      getAll(GET_FIRST_TEN_NOTIFICATIONS).then((response) => {
        const formattedResponse = formatNotifications(response.notifications);
        setNotifications(formattedResponse);
        setTotalUnreadNotifications(response.totalUnreadCount);
      });
    }
  }, []);

  const toggleAnchor = (event) => {
    if (isDisabled) {
      return;
    }
    if (anchor) {
      setAnchor(null);
    } else {
      setAnchor(event.currentTarget);
    }
  };
  const toggleNotificationMenu = (event) => {
    if (notificationAnchor) {
      setNotificationAnchor(null);
    } else {
      setNotificationAnchor(event.currentTarget);
    }
  };

  const viewOrder = async (notificationId, orderId) => {
    setNotificationAnchor(null);
    navigate(DefaultRoutes.orderInfo.replace(":orderId", encryptId(orderId)));
    await postItem(
      `${NOTIFICATIONS_URL}/${notificationId}/read`,
      {},
      () => {},
      "",
      "Failed to mark notification as read",
      false
    );
    const updatedNotifications = notifications?.map((notification) => {
      if (notification.id === notificationId) {
        return { ...notification, read: true };
      }
      return notification;
    });
    setNotifications(updatedNotifications);
    setTotalUnreadNotifications((prev) => prev - 1);
  };

  const logout = useLogout();

  return (
    <NavigationBarStyles isDisabled={isDisabled}>
      <Box className="appBar">
        <Toolbar className="logoToolbar">
          <Grid container alignItems="center" justifyContent="space-between">
            <LogoTitle isDisabled={isDisabled} />
            <Box display="flex" alignItems="center">
              {user.roles[0] !== "ADMIN" && (
                <IconButton onClick={toggleNotificationMenu} color="inherit">
                  <Badge badgeContent={totalUnreadNotifications} color="error">
                    <NotificationsIcon color={"primary"} />
                  </Badge>
                </IconButton>
              )}
              {/* Notification Menu */}
              <UserMenu
                anchorEl={notificationAnchor}
                open={Boolean(notificationAnchor)}
                onClose={() => setNotificationAnchor(null)}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                {notifications?.length === 0 ? (
                  <MenuItem
                    sx={{
                      justifyContent: "center", // Center the text
                      backgroundColor: "#fafafa", // Optional: Light background
                    }}
                  >
                    <ListItemText
                      primary="No Notifications"
                      primaryTypographyProps={{
                        fontWeight: "bold", // Make the text bold
                        textAlign: "center", // Center-align the text
                      }}
                    />
                  </MenuItem>
                ) : (
                  notifications?.map((notification, index) => (
                    <MenuItem
                      key={notification.id}
                      onClick={() =>
                        viewOrder(notification.id, notification.orderId)
                      }
                      sx={{
                        borderBottom:
                          index !== notifications?.length - 1
                            ? "1px solid #e0e0e0"
                            : "none",
                        backgroundColor: notification.read
                          ? "inherit"
                          : "#fafafa",
                      }}
                    >
                      <ListItemText
                        primary={notification.subject}
                        secondary={notification.message}
                        primaryTypographyProps={{
                          fontWeight: "bold", // Make the primary text bold
                        }}
                        sx={{
                          padding: "8px 16px",
                        }}
                      />
                    </MenuItem>
                  ))
                )}
              </UserMenu>
              <Box className="userSection" onClick={toggleAnchor}>
                <Avatar src="/profileAvatarLight.jpg" className="avatar" />
                <Typography className="userName" variant="body1">
                  {user.username}
                </Typography>
                <UserMenu
                  anchorEl={anchor}
                  open={Boolean(anchor)}
                  onClose={() => setAnchor(null)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  PopoverClasses={{
                    root: "popoverRootOverride",
                    paper: "menuPaper",
                  }}
                  MenuListProps={{
                    className: "menuList",
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      setAnchor(null);
                      navigate("/profile");
                    }}
                  >
                    <ListItemIcon>
                      <AccountCircle fontSize="small" />
                    </ListItemIcon>
                    Profile
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      toggleAnchor();
                      logout();
                    }}
                  >
                    <ListItemIcon>
                      <ExitToApp fontSize="small" color={"error"} />
                    </ListItemIcon>
                    Logout
                  </MenuItem>
                </UserMenu>
              </Box>
            </Box>
          </Grid>
        </Toolbar>
      </Box>
    </NavigationBarStyles>
  );
};

export default NavigationBar;
